import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  LinearProgress,
  useTheme
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  Coffee as BreakIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import { Staff } from '../../types';

interface ScheduleSummaryProps {
  staff: Staff[];
  weekDates: Date[];
  getStaffScheduleForWeek: (staff: Staff) => { [key: string]: any };
  calculateWorkHours: (startTime: string, endTime: string, breakStart?: string, breakEnd?: string) => number;
}

const ScheduleSummary: React.FC<ScheduleSummaryProps> = ({
  staff,
  weekDates,
  getStaffScheduleForWeek,
  calculateWorkHours
}) => {
  const theme = useTheme();
  // Calculate summary statistics
  const totalStaff = staff.length;
  const weekDateKeys = weekDates.map(d => d.toISOString().split('T')[0]);
  const totalScheduledDays = staff.reduce((total, staffMember) => {
    const schedules = getStaffScheduleForWeek(staffMember);
    const scheduledThisWeek = Object.keys(schedules).filter(key => weekDateKeys.includes(key)).length;
    return total + scheduledThisWeek;
  }, 0);

  const totalWorkHours = staff.reduce((total, staffMember) => {
    const schedules = getStaffScheduleForWeek(staffMember);
    const staffTotal = Object.values(schedules).reduce((staffHours: number, schedule: any) => {
      return staffHours + calculateWorkHours(
        schedule.startTime,
        schedule.endTime,
        schedule.startBreak,
        schedule.endBreak
      );
    }, 0);
    return total + staffTotal;
  }, 0);

  const averageHoursPerStaff = totalStaff > 0 ? totalWorkHours / totalStaff : 0;
  const scheduleCompletionRate = totalStaff > 0 ? (totalScheduledDays / (totalStaff * 7)) * 100 : 0;

  // Find busiest and quietest days
  const dayStats = weekDates.map((date) => {
    const dayKey = date.toISOString().split('T')[0];
    const staffWorking = staff.filter(staffMember => {
      const schedules = getStaffScheduleForWeek(staffMember);
      return schedules[dayKey];
    }).length;
    
    const totalHours = staff.reduce((dayTotal, staffMember) => {
      const schedules = getStaffScheduleForWeek(staffMember);
      const schedule = schedules[dayKey];
      if (schedule) {
        return dayTotal + calculateWorkHours(
          schedule.startTime,
          schedule.endTime,
          schedule.startBreak,
          schedule.endBreak
        );
      }
      return dayTotal;
    }, 0);

    return {
      date,
      dayName: date.toLocaleDateString('en-US', { weekday: 'long' }),
      staffWorking,
      totalHours
    };
  });

  const busiestDay = dayStats.reduce((prev, current) => 
    current.totalHours > prev.totalHours ? current : prev
  );
  
  const quietestDay = dayStats.reduce((prev, current) => 
    current.totalHours < prev.totalHours ? current : prev
  );

  return (
    <Grid container spacing={3} alignItems="stretch">
      {/* Overall Statistics */}
      <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
        <Card sx={{ 
          flex: 1,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          bgcolor: 'background.paper',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4]
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  mr: 2
                }}
              >
                <WorkIcon fontSize="small" />
              </Box>
              <Typography variant="h6" fontWeight={600}>Total Hours</Typography>
            </Box>
            <Typography variant="h4" color="primary.main" fontWeight={700}>
              {totalWorkHours.toFixed(0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This week
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
        <Card sx={{ 
          flex: 1,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          bgcolor: 'background.paper',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4]
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: theme.palette.secondary.main,
                  color: 'white',
                  mr: 2
                }}
              >
                <TimeIcon fontSize="small" />
              </Box>
              <Typography variant="h6" fontWeight={600}>Avg Hours/Staff</Typography>
            </Box>
            <Typography variant="h4" color="secondary.main" fontWeight={700}>
              {averageHoursPerStaff.toFixed(1)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Per week
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
        <Card sx={{ 
          flex: 1,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          bgcolor: 'background.paper',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4]
          }
        }}>
          <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: theme.palette.success.main,
                  color: 'white',
                  mr: 2
                }}
              >
                <ScheduleIcon fontSize="small" />
              </Box>
              <Typography variant="h6" fontWeight={600}>Schedule Rate</Typography>
            </Box>
            <Typography variant="h4" color="success.main" fontWeight={700}>
              {scheduleCompletionRate.toFixed(0)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Days scheduled
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(scheduleCompletionRate, 100)} 
              sx={{ 
                mt: 'auto',
                pt: 2,
                height: 6,
                borderRadius: 3,
                backgroundColor: theme.palette.success.light + '30',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3
                }
              }}
              color="success"
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
        <Card sx={{ 
          flex: 1,
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          bgcolor: 'background.paper',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4]
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: theme.palette.warning.main,
                  color: 'white',
                  mr: 2
                }}
              >
                <BreakIcon fontSize="small" />
              </Box>
              <Typography variant="h6" fontWeight={600}>Staff Count</Typography>
            </Box>
            <Typography variant="h4" color="warning.main" fontWeight={700}>
              {totalStaff}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total staff members
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Daily Overview */}
      <Grid item xs={12}>
        <Card sx={{ 
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Daily Overview
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              {dayStats.map((day) => (
                <Grid item xs={12} sm={6} md key={day.date.getTime()} sx={{ display: 'flex' }}>
                  <Box 
                    sx={{ 
                      flex: 1,
                      p: 2.5, 
                      border: 1, 
                      borderColor: day === busiestDay ? 'success.main' : 
                                   day === quietestDay ? 'warning.main' : 'divider', 
                      borderRadius: 1.5,
                      bgcolor: day === busiestDay ? 'success.light' : 
                               day === quietestDay ? 'warning.light' : 'background.default',
                      color: day === busiestDay || day === quietestDay ? 'white' : 'inherit',
                      transition: 'all 0.3s ease-in-out',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[3]
                      }
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {day.dayName}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                      {day.date.toLocaleDateString()}
                    </Typography>
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {day.staffWorking} staff working
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {day.totalHours.toFixed(1)} total hours
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 'auto', pt: 1, minHeight: 32, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      {day === busiestDay && (
                        <Chip 
                          label="Busiest" 
                          size="small" 
                          sx={{ 
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 600
                          }} 
                        />
                      )}
                      {day === quietestDay && (
                        <Chip 
                          label="Quietest" 
                          size="small" 
                          sx={{ 
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 600
                          }} 
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Quick Insights */}
      <Grid item xs={12}>
        <Card sx={{ 
          borderRadius: 2,
          boxShadow: theme.shadows[1],
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Quick Insights
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 3,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'background.default',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[3]
                  }
                }}>
                  <Typography variant="h4" color="primary.main" fontWeight={700}>
                    {busiestDay.dayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Busiest Day ({busiestDay.totalHours.toFixed(1)} hrs)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 3,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'background.default',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    borderColor: 'warning.main',
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[3]
                  }
                }}>
                  <Typography variant="h4" color="warning.main" fontWeight={700}>
                    {quietestDay.dayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Quietest Day ({quietestDay.totalHours.toFixed(1)} hrs)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 3,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'background.default',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    borderColor: 'success.main',
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[3]
                  }
                }}>
                  <Typography variant="h4" color="success.main" fontWeight={700}>
                    {staff.filter(s => {
                      const schedules = getStaffScheduleForWeek(s);
                      return Object.keys(schedules).length === 7;
                    }).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Full Week Scheduled
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ScheduleSummary;
